import { isBorderStyle, type BorderStyle } from "./border-settings";

export const MERMAID_THEME_STORAGE_KEY = "turbonotes2-mermaid-theme";
export const MERMAID_BORDER_STORAGE_KEY = "turbonotes2-mermaid-border";
export const MERMAID_ALIGN_STORAGE_KEY = "turbonotes2-mermaid-align";
export const MERMAID_WIDTH_STORAGE_KEY = "turbonotes2-mermaid-width";
// Kept so diagrams saved before border styles were shared retain their appearance.
export const MERMAID_FRAME_STORAGE_KEY = "turbonotes2-mermaid-frame";

export const MERMAID_THEMES = ["editor", "default", "dark", "forest", "neutral"] as const;
export const MERMAID_FRAMES = ["square", "double", "none"] as const;
export const MERMAID_ALIGNS = ["left", "center", "right"] as const;
export const MERMAID_WIDTHS = ["stretch", "compact"] as const;

export type MermaidTheme = (typeof MERMAID_THEMES)[number];
export type MermaidFrame = (typeof MERMAID_FRAMES)[number];
export type MermaidAlign = (typeof MERMAID_ALIGNS)[number];
export type MermaidWidth = (typeof MERMAID_WIDTHS)[number];

export interface MermaidSettings {
  theme: MermaidTheme;
  border: BorderStyle;
  align: MermaidAlign;
  width: MermaidWidth;
}

export const DEFAULT_MERMAID_SETTINGS: Readonly<MermaidSettings> = {
  theme: "editor",
  border: "thin",
  align: "left",
  width: "compact",
};

export const isMermaidTheme = (value: unknown): value is MermaidTheme =>
  MERMAID_THEMES.includes(value as MermaidTheme);
export const isMermaidFrame = (value: unknown): value is MermaidFrame =>
  MERMAID_FRAMES.includes(value as MermaidFrame);
export const isMermaidBorder = isBorderStyle;
export const isMermaidAlign = (value: unknown): value is MermaidAlign =>
  MERMAID_ALIGNS.includes(value as MermaidAlign);
export const isMermaidWidth = (value: unknown): value is MermaidWidth =>
  MERMAID_WIDTHS.includes(value as MermaidWidth);

export function loadMermaidSettings(storage: Pick<Storage, "getItem">): MermaidSettings {
  const theme = storage.getItem(MERMAID_THEME_STORAGE_KEY);
  const border = storage.getItem(MERMAID_BORDER_STORAGE_KEY);
  const frame = storage.getItem(MERMAID_FRAME_STORAGE_KEY);
  const align = storage.getItem(MERMAID_ALIGN_STORAGE_KEY);
  const width = storage.getItem(MERMAID_WIDTH_STORAGE_KEY);
  return {
    theme: isMermaidTheme(theme) ? theme : DEFAULT_MERMAID_SETTINGS.theme,
    border: isMermaidBorder(border) ? border
      : isMermaidFrame(frame) ? frame === "square" ? "thin" : frame
        : DEFAULT_MERMAID_SETTINGS.border,
    align: isMermaidAlign(align) ? align : DEFAULT_MERMAID_SETTINGS.align,
    width: isMermaidWidth(width) ? width : DEFAULT_MERMAID_SETTINGS.width,
  };
}
